import * as THREE from 'three';
import { materialPool } from './MaterialPool';

// ============================================================
// Speed lines — streaks that fly past when going fast
// ============================================================

const MAX_LINES = 40;
const LINE_LENGTH = 20;
const LINE_RADIUS = 0.1;
const LINE_LIFETIME = 0.3;
const SPAWN_RADIUS = 15;     // how far from aircraft they spawn
const SPEED_THRESHOLD = 100; // minimum speed to show lines

const lineGeom = new THREE.CylinderGeometry(LINE_RADIUS, LINE_RADIUS, LINE_LENGTH, 3, 1);
lineGeom.rotateX(Math.PI / 2);

interface SpeedLine {
  readonly mesh: THREE.Mesh;
  age: number;
}

const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();

export class SpeedLineSystem {
  private readonly scene: THREE.Scene;
  private readonly lines: SpeedLine[] = [];
  private spawnTimer = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  update(aircraft: THREE.Object3D, speed: number, dt: number): void {
    if (speed < SPEED_THRESHOLD) {
      this.spawnTimer = 0;
    } else {
      // Spawn rate scales with speed
      const rate = (speed - SPEED_THRESHOLD) / 50;
      this.spawnTimer += rate * dt;
      while (this.spawnTimer > 0.05 && this.lines.length < MAX_LINES) {
        this.spawnTimer -= 0.05;
        this.spawnLine(aircraft);
      }
    }

    // Update existing lines
    for (let i = this.lines.length - 1; i >= 0; i--) {
      const l = this.lines[i];
      l.age += dt;
      if (l.age >= LINE_LIFETIME) {
        this.scene.remove(l.mesh);
        materialPool.release(l.mesh.material as THREE.MeshBasicMaterial);
        this.lines.splice(i, 1);
        continue;
      }
      (l.mesh.material as THREE.MeshBasicMaterial).opacity = 0.4 * (1 - l.age / LINE_LIFETIME);
    }
  }

  private spawnLine(aircraft: THREE.Object3D): void {
    _forward.set(0, 0, -1).applyQuaternion(aircraft.quaternion);
    _right.set(1, 0, 0).applyQuaternion(aircraft.quaternion);
    _up.set(0, 1, 0).applyQuaternion(aircraft.quaternion);

    // Random position around the aircraft, slightly ahead
    const rx = (Math.random() - 0.5) * 2 * SPAWN_RADIUS;
    const ry = (Math.random() - 0.5) * 2 * SPAWN_RADIUS;

    const pos = aircraft.position.clone()
      .addScaledVector(_forward, 10 + Math.random() * 20)
      .addScaledVector(_right, rx)
      .addScaledVector(_up, ry);

    const material = materialPool.acquire();
    material.color.set(0xffffff);
    material.opacity = 0.4;

    const mesh = new THREE.Mesh(lineGeom, material);
    mesh.position.copy(pos);
    mesh.lookAt(pos.clone().add(_forward));
    mesh.frustumCulled = false;

    this.scene.add(mesh);
    this.lines.push({ mesh, age: 0 });
  }

  dispose(): void {
    for (const l of this.lines) {
      this.scene.remove(l.mesh);
      materialPool.release(l.mesh.material as THREE.MeshBasicMaterial);
    }
    this.lines.length = 0;
  }
}
